import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { VisitService } from "../services/VisitService";
import { useAccount } from "./AccountContext";
import { Visit, DashboardResponse } from "@visitproof/types";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// Re-type for backward compatibility
type DashboardData = DashboardResponse;

interface DashboardContextProps {
  dashboardData: DashboardData | null;
  visits: Visit[];
  reloadDashboards: (
    timeFrame: "last_week" | "last_month" | "last_quarter",
  ) => void;
  loading: boolean;
  error: string | null;
}

const DashboardContext = createContext<DashboardContextProps | undefined>(
  undefined,
);

export const useDashboard = (): DashboardContextProps => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
};

export const DashboardProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { account } = useAccount();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null,
  );
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const reloadDashboards = useCallback(async (
    timeFrame: "last_week" | "last_month" | "last_quarter",
  ) => {
    // Only load dashboard data if we have an account (admin users)
    if (!account?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const fetchDashboard = httpsCallable(functions, "fetchDashboard");
      const { data } = await fetchDashboard({ timeFrame });
      const visitList = await VisitService.getUpcomingVisits(account.id);
      setDashboardData(data as DashboardData);
      setVisits(visitList);
    } catch (err) {
      setError("Failed to load visits");
    } finally {
      setLoading(false);
    }
  }, [account?.id]);

  return (
    <DashboardContext.Provider
      value={{
        dashboardData,
        visits,
        reloadDashboards,
        loading,
        error,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
